import { ClientType, Gender, Register } from "@/types";
import { useState, useEffect } from "react";
import {
    useForm,
    FormProvider,
    SubmitHandler,
    Controller,
} from "react-hook-form";
import { PiXLight } from "react-icons/pi";
import {
    Button,
    Modal,
    ActionIcon,
    Input,
    Textarea,
    Text,
    Password,
} from "rizzui";
import { useNavigate } from "react-router-dom";
import routes from "@/routers/routes";
import { CreateClient } from "@/server/api-services";
import toast from "react-hot-toast";
import FormFooter from "@/components/form-footer";

const AddClientPage = () => {
    const navigate = useNavigate();
    const [isLoading, setLoading] = useState(false);

    const methods = useForm<Register>({
        defaultValues: {
            fullName: "",
            mobile: "",
            title: "",
            email: "",
            password: "",
            confirmPassword: "",
            UserType: ClientType.Agent,
            Address: "",
            placeOfBirth: "",
            dateOfBirth: "",
            gender: Gender.Male,
        },
    });

    const onSubmit: SubmitHandler<Register> = async (data) => {
        setLoading(true);
        try {
            const response = await CreateClient(data);
            if (response.statusCode !== 200) {
                toast.error("Failed to add client");
                return;
            }
            toast.success("Client added successfully");
            methods.reset();
            navigate(routes.clients.list);
        } catch (error) {
            toast.error("Failed to add client");
            console.error("Failed to add client", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="@container">
            <div className="mb-4 flex items-start justify-between mt-5">
                <div className="w=1/3">
                    <div className="flex flex-col ">
                        <Text className="text-lg font-semibold h3">
                            Add Client
                        </Text>
                        <p className="text-sm text-gray-500">
                            Add a new client, fill in the details below to add a
                            new client
                        </p>
                        <div className="mt-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <Text className="text-sm text-gray-500">
                                        Fields marked with * are required
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="w-2/3">
                    <FormProvider {...methods}>
                        <form onSubmit={methods.handleSubmit(onSubmit)}>
                            <div className="mb-4 mt-2">
                                <Input
                                    label="Full Name"
                                    {...methods.register("fullName")}
                                    error={
                                        methods.formState.errors.fullName
                                            ?.message
                                    }
                                />
                            </div>

                            <div className="mb-4 mt-2">
                                <Input
                                    label="Title"
                                    {...methods.register("title")}
                                    error={
                                        methods.formState.errors.title?.message
                                    }
                                />
                            </div>

                            <div className="mb-4 flex items-center justify-between">
                                <div className="w-1/2  pe-2">
                                    <Input
                                        label="Email"
                                        {...methods.register("email")}
                                        error={
                                            methods.formState.errors.email
                                                ?.message
                                        }
                                    />
                                </div>

                                <div className="w-1/2">
                                    <Input
                                        label="Mobile"
                                        {...methods.register("mobile")}
                                        error={
                                            methods.formState.errors.mobile
                                                ?.message
                                        }
                                    />
                                </div>
                            </div>
                            <div className="mb-2 mt-2">
                                <Input
                                    label="Address"
                                    {...methods.register("Address")}
                                    error={
                                        methods.formState.errors.Address
                                            ?.message
                                    }
                                />
                            </div>
                            <div className="mb-4 flex items-center justify-between">
                                <div className="w-1/2 mb-4 mt-2 pe-2">
                                    <Input
                                        label="Place of Birth"
                                        {...methods.register("placeOfBirth")}
                                        error={
                                            methods.formState.errors
                                                .placeOfBirth?.message
                                        }
                                    />
                                </div>

                                <div className="w-1/2 mb-4 mt-2">
                                    <Controller
                                        control={methods.control}
                                        name="gender"
                                        render={({ field }) => (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Gender
                                                </label>
                                                <select
                                                    {...field}
                                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                                >
                                                    <option value={Gender.Male}>
                                                        Male
                                                    </option>
                                                    <option
                                                        value={Gender.Female}
                                                    >
                                                        Female
                                                    </option>
                                                </select>
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="mb-4 flex items-center justify-between">
                                <div className="w-1/2 pe-2">
                                    <Input
                                        label="Date of Birth"
                                        type="date"
                                        {...methods.register("dateOfBirth")}
                                        error={
                                            methods.formState.errors.dateOfBirth
                                                ?.message
                                        }
                                    />
                                </div>
                                <div className="w-1/2 ">
                                    <Controller
                                        control={methods.control}
                                        name="UserType"
                                        render={({ field }) => (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    User Type
                                                </label>
                                                <select
                                                    {...field}
                                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                                >
                                                    <option
                                                        value={ClientType.Agent}
                                                    >
                                                        Agent
                                                    </option>
                                                    <option
                                                        value={ClientType.Owner}
                                                    >
                                                        Owner
                                                    </option>
                                                </select>
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="mb-4 flex items-center justify-between">
                                <div className="w-1/2 mb-4 mt-2 pe-2">
                                    <Password
                                        label="Confirm Password"
                                        placeholder="Confirm your password"
                                        size="lg"
                                        className="[&>label>span]:font-medium"
                                        inputClassName="text-sm"
                                        {...methods.register("confirmPassword")}
                                        error={
                                            methods.formState.errors
                                                .confirmPassword?.message
                                        }
                                    />
                                </div>

                                <div className="w-1/2 mb-4 mt-2">
                                    <Password
                                        label="Password"
                                        placeholder="Enter your password"
                                        size="lg"
                                        className="[&>label>span]:font-medium"
                                        inputClassName="text-sm"
                                        {...methods.register("password")}
                                        error={
                                            methods.formState.errors.password
                                                ?.message
                                        }
                                    />
                                </div>
                            </div>

                            <FormFooter
                                isLoading={isLoading}
                                altBtnText="Back to Clients"
                                handleAltBtn={() =>
                                    navigate(routes.clients.list)
                                }
                                submitBtnText=" Add Client"
                            />
                        </form>
                    </FormProvider>
                </div>
            </div>
        </div>
    );
};

export default AddClientPage;
